<script setup lang="ts">
import {computed, onUnmounted, ref, watch} from 'vue';
import type {Part, PolySynth} from 'tone';
import {Midi} from '@tonejs/midi';
import {Icon} from '@iconify/vue';
import {useNotificationStore} from '@/stores/notifications';
import {apiClient} from '@/api-client';

// Tone.js wird bewusst NICHT statisch importiert: der Modul-Import initialisiert eager den
// globalen Transport und damit den AudioContext. Läuft das beim App-Start (dieser Player wird
// eager gerendert), warnt Chrome mit „The AudioContext was not allowed to start. It must be
// resumed (or created) after a user gesture" – noch bevor der Nutzer irgendetwas geklickt hat.
// Stattdessen wird Tone erst beim ersten Abspielen (nach User-Geste) dynamisch geladen.
let tone: typeof import('tone') | null = null;
async function loadTone(): Promise<typeof import('tone')> {
  if (!tone) tone = await import('tone');
  return tone;
}

// Define MIDI event type
interface MidiEvent {
  time: number;
  duration: number;
  midi: number;
  velocity: number;
}

// Stateless: der rohe MusicXML-Inhalt wird serverseitig in MIDI gewandelt (POST /api/midi).
// So ist die Wiedergabe immer abspielbar – passend zum aktuell angezeigten Inhalt
// (inkl. Transposition/Shift/Edit), ohne file_id-Abhängigkeit oder serverseitige Ablage.
const {musicXml = undefined} = defineProps<{
  musicXml?: string;
}>();

const canPlay = computed(() => !!(musicXml && musicXml.trim()));

const notificationStore = useNotificationStore();

const isPlaying = ref(false);
const isLoading = ref(false);

// Keep track of Tone.js instances to properly dispose of them
let synth: PolySynth | null = null;
let part: Part | null = null;

// Cleanup on component unmount
onUnmounted(() => {
  cleanup();
});

// Watch for content changes to reset the player
watch(() => musicXml, () => {
  stop();
});

function cleanup() {
  if (synth) {
    synth.releaseAll();
    synth.dispose();
    synth = null;
  }
  if (part) {
    part.dispose();
    part = null;
  }

  // Nur aufräumen, wenn Tone bereits geladen wurde – sonst existiert kein Transport/Context.
  if (tone) {
    tone.getTransport().stop();
    tone.getTransport().cancel();
  }
}

// Type guard for MidiEvent array (Chord)
function isChordEvent(event: unknown): event is MidiEvent[] {
  return (
    Array.isArray(event) &&
    event.every(e => 
      typeof e === 'object' &&
      e !== null &&
      'time' in e &&
      'duration' in e &&
      'midi' in e &&
      'velocity' in e
    )
  );
}

async function loadAndPlay() {
  if (!canPlay.value) {
    notificationStore.notify('Keine MIDI-Quelle verfügbar', 'warning');
    return;
  }

  isLoading.value = true;

  try {
    // Tone.js erst hier (nach der User-Geste) laden und den Audio-Context starten.
    const Tone = await loadTone();
    await Tone.start();

    // MIDI-Daten stateless aus dem rohen MusicXML beziehen (POST /api/midi).
    const arrayBuffer = await apiClient.fetchMidiFromXml(musicXml as string);

    const midi = new Midi(arrayBuffer);

    // Clean up any existing parts/synths
    cleanup();

    // Create a polyphonic synthesizer
    synth = new Tone.PolySynth(Tone.Synth, {
      envelope: {
        attack: 0.02,
        decay: 0.1,
        sustain: 0.3,
        release: 1,
      },
    }).toDestination();
    
    synth.maxPolyphony = 32; // Ensure enough voices for chords

    // Collect all notes from all tracks
    const allNotes: MidiEvent[] = [];
    
    midi.tracks.forEach((track) => {
      track.notes.forEach((note) => {
        allNotes.push({
          time: note.time + 0.05, // Small offset to ensure first note plays
          duration: note.duration,
          midi: note.midi,
          velocity: note.velocity,
        });
      });
    });

    if (allNotes.length === 0) {
      throw new Error('MIDI Datei enthält keine Noten');
    }

    // Group notes by time to create chords
    // We use a Map with a quantized time key to handle float precision issues
    const chordsMap = new Map<string, MidiEvent[]>();
    const timeEpsilon = 0.01; // 10ms window for grouping

    allNotes.sort((a, b) => a.time - b.time);

    allNotes.forEach(note => {
      // Find an existing time key that is close enough
      let foundKey: string | undefined;
      for (const key of chordsMap.keys()) {
        const keyTime = parseFloat(key);
        if (Math.abs(keyTime - note.time) < timeEpsilon) {
          foundKey = key;
          break;
        }
      }

      const key = foundKey || note.time.toString();
      if (!chordsMap.has(key)) {
        chordsMap.set(key, []);
      }
      chordsMap.get(key)!.push(note);
    });

    // Convert map to array of [time, events[]] for Tone.Part
    const chordEvents = Array.from(chordsMap.entries()).map(([timeStr, notes]) => {
      // Use the time of the first note in the group as the event time
      // (or the average, but first note time is simpler and sufficient)
      const time = notes?.[0]?.time;
      return [time, notes] as [number, MidiEvent[]];
    });

    // Create a Part with the chord events
    part = new Tone.Part((time, chord: unknown) => {
      if (synth && isChordEvent(chord)) {
        // Trigger all notes in the chord
        // We can optimize by grouping by duration if needed, but iterating is fine
        chord.forEach(note => {
          synth!.triggerAttackRelease(
            Tone.Frequency(note.midi, 'midi').toFrequency(),
            note.duration,
            time,
            note.velocity
          );
        });
      }
    }, chordEvents);

    part.start(0);
    part.loop = false;

    // Set up end callback
    const endTime = Math.max(...allNotes.map(e => e.time + e.duration));
    Tone.getTransport().schedule(() => {
      stop();
    }, endTime + 0.5); // Add a little buffer at the end

    // Start playback
    Tone.getTransport().start();
    isPlaying.value = true;

    notificationStore.notify('MIDI Wiedergabe gestartet', 'success', 1500);
  } catch (error) {
    console.error('Failed to load/play MIDI:', error);
    notificationStore.notify(
      error instanceof Error ? error.message : 'MIDI Wiedergabe fehlgeschlagen',
      'error'
    );
    cleanup();
    throw error;
  } finally {
    isLoading.value = false;
  }
}

function stop() {
  tone?.getTransport().stop();
  // Also release any currently playing notes
  if (synth) {
    synth.releaseAll();
  }
  isPlaying.value = false;
}

function toggle() {
  if (isPlaying.value) {
    stop();
  } else {
    loadAndPlay();
  }
}
</script>

<template>
  <div class="midi-player inline-flex">
    <button
      @click="toggle"
      :disabled="!canPlay || isLoading"
      class="group relative inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md font-medium text-sm transition-all duration-300 shadow-sm hover:shadow-md disabled:shadow-none overflow-hidden"
      :class="[
        isPlaying
          ? 'bg-red-50 text-red-600 border border-red-100 hover:bg-red-100'
          : 'bg-green-50 text-green-800 border border-green-200 hover:border-blue-300 hover:text-blue-600',
        !canPlay || isLoading ? 'opacity-50 cursor-not-allowed bg-gray-50' : ''
      ]"
      type="button"
    >
      <!-- Background Progress/Visualizer Placeholder (Optional) -->
      <div v-if="isPlaying" class="absolute inset-0 bg-red-100/30 animate-pulse"></div>

      <div class="relative flex items-center gap-2">
        <Icon
          v-if="isLoading"
          icon="fa6-solid:spinner"
          class="animate-spin text-blue-500"
        />
        <Icon
          v-else-if="isPlaying"
          icon="fa6-solid:stop"
          class="text-xs"
        />
        <Icon
          v-else
          icon="fa6-solid:play"
          class="text-xs ml-0.5"
        />
        
        <span>
          {{ isLoading ? 'Laden...' : isPlaying ? 'Stop' : 'Anhören' }}
        </span>
      </div>
    </button>
  </div>
</template>

<style scoped>
/* No scoped styles needed, using Tailwind utility classes */
</style>
